import { unbindElement } from "../brand/apply";
import type { BrandTokens } from "../brand/tokens";
import {
  type FormatPreset,
  getPreset,
  PRESET_CATEGORY_LABELS,
  type PresetCategory,
  VIDEO_PRESETS,
} from "../scene/presets";
import type { SceneDocument } from "../scene/schema";
import { changeVideoFormat } from "../video/reformat";
import { buildTemplateDocument, type TemplateLogo, type TextMeasure } from "./builder";
import { LIBRARY_TEMPLATES, type LibraryTemplate, TEMPLATE_TOPIC_LABELS } from "./library";
import { BRAND_STYLES, brandStyleTokens, getBrandStyle } from "./styles";

export * from "./builder";
export * from "./library";
export * from "./styles";

/** Prefix of library template ids where they share a field with document ids. */
export const LIBRARY_TEMPLATE_PREFIX = "library:";

export function getLibraryTemplate(id: string): LibraryTemplate | undefined {
  const key = id.startsWith(LIBRARY_TEMPLATE_PREFIX)
    ? id.slice(LIBRARY_TEMPLATE_PREFIX.length)
    : id;
  return LIBRARY_TEMPLATES.find((template) => template.id === key);
}

/** Whether a library template shows a device mockup (phone, laptop, browser …). */
export function libraryTemplateHasDevice(template: LibraryTemplate): boolean {
  return template.pages.some((page) => page.nodes.some((node) => node.kind === "device"));
}

export function libraryTemplateCategory(template: LibraryTemplate): PresetCategory {
  return getPreset(template.presetId)?.category ?? "social";
}

export interface LibraryBuildOptions {
  /**
   * Kit of the target project: the document uses its colours, fonts and logo
   * and stays linked to it. Without a kit the template's design style (or
   * `style`) supplies the values, and the document is not linked (like any
   * new file without a kit, it does not change when a kit is created later).
   */
  tokens?: BrandTokens | null;
  /** Design style while there is no kit (`BRAND_STYLES`); default: the template's own. */
  style?: string | undefined;
  logos?: readonly TemplateLogo[];
  /** Word mark where the kit has no logo (usually the organization's name). */
  brandName?: string | undefined;
  /** Exact text measurement (browser, fonts loaded); otherwise estimated. */
  measure?: TextMeasure | undefined;
  /**
   * Video templates: another video format (e.g. `video-landscape`); the
   * template is laid out for it like "Format ändern" does.
   */
  presetId?: string | undefined;
}

/**
 * What the search matches for a library template: title, description,
 * topic, format and category ("Video", "Druck") and its extra search words.
 */
export function libraryTemplateSearchFields(template: LibraryTemplate): string[] {
  const preset = getPreset(template.presetId);
  return [
    template.title,
    template.description,
    TEMPLATE_TOPIC_LABELS[template.topic],
    preset?.label ?? "",
    preset ? PRESET_CATEGORY_LABELS[preset.category] : "",
    ...(template.tags ?? []),
  ];
}

/** Formats a library template can be created in: video templates in every video format. */
export function libraryTemplateFormats(template: LibraryTemplate): FormatPreset[] {
  const own = getPreset(template.presetId);
  if (!own) return [];
  return own.kind === "video" ? [...VIDEO_PRESETS] : [own];
}

/** Tokens a library template is built with. */
export function libraryTokens(
  template: LibraryTemplate,
  options: Pick<LibraryBuildOptions, "tokens" | "style">,
): BrandTokens {
  if (options.tokens) return options.tokens;
  const style =
    (options.style ? getBrandStyle(options.style) : undefined) ??
    getBrandStyle(template.style) ??
    BRAND_STYLES[0]!;
  return brandStyleTokens(style);
}

/** A new document from a library template. */
export function buildLibraryTemplate(
  template: LibraryTemplate,
  options: LibraryBuildOptions = {},
): SceneDocument {
  const linked = Boolean(options.tokens);
  const doc = buildTemplateDocument(template.presetId, template.pages, {
    tokens: libraryTokens(template, options),
    linked,
    logos: linked ? (options.logos ?? []) : [],
    brandName: options.brandName?.trim() || "Dein Logo",
    measure: options.measure,
  });
  if (!linked) {
    // The builder links nothing without a kit; this also covers captions presets.
    for (const element of Object.values(doc.elements)) unbindElement(element);
  }
  const target = options.presetId ? getPreset(options.presetId) : undefined;
  if (
    target &&
    target.id !== template.presetId &&
    target.kind === "video" &&
    doc.kind === "video"
  ) {
    changeVideoFormat(doc, target);
  }
  return doc;
}
